import { useEffect, useState } from "react";
import { api, eur } from "@/lib/rentApi";
import { PageHeader, EmptyState, Loading } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Building2, MapPin, CreditCard, CheckCircle2, Percent } from "lucide-react";
import { toast } from "sonner";

export default function Billing() {
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState(false);

  const load = () => api.get("/billing/summary").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const pay = async () => {
    setPaying(true);
    try {
      const { data: d } = await api.post("/billing/checkout", { origin_url: window.location.origin });
      window.location.href = d.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Paiement indisponible");
      setPaying(false);
    }
  };

  if (!data) return <Loading />;

  return (
    <div>
      <PageHeader
        title="Facturation"
        subtitle="Votre abonnement Rent — 150 € / an par adresse, tout compris."
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="p-5 rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Tarif</span><Percent className="w-4 h-4 text-[hsl(var(--ring))]" /></div>
          <p className="font-heading text-2xl font-bold mt-2">{eur(data.price_per_address)}<span className="text-sm font-normal text-muted-foreground"> /an /adresse</span></p>
        </div>
        <div className="p-5 rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Adresses · Lots</span><Building2 className="w-4 h-4 text-[hsl(var(--ring))]" /></div>
          <p className="font-heading text-2xl font-bold mt-2" data-testid="billing-count">{data.nb_addresses} · {data.nb_lots}</p>
        </div>
        <div className="p-5 rounded-xl border border-border bg-card">
          <span className="text-sm text-muted-foreground">Total annuel</span>
          <p className="font-heading text-2xl font-bold mt-2" data-testid="billing-total">{eur(data.annual_total)}</p>
        </div>
      </div>

      {data.addresses.length === 0 ? (
        <EmptyState title="Aucune adresse" desc="Ajoutez un bien pour démarrer votre abonnement." />
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <h3 className="font-heading font-semibold">Adresses facturées</h3>
            <Button className="rounded-full gap-2" onClick={pay} disabled={paying} data-testid="pay-subscription-btn">
              <CreditCard className="w-4 h-4" /> {paying ? "…" : `Régler ${eur(data.annual_total)}`}
            </Button>
          </div>
          <Table>
            <TableHeader><TableRow><TableHead>Adresse</TableHead><TableHead>Lots</TableHead><TableHead className="text-right">Montant /an</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.addresses.map((a) => (
                <TableRow key={a.key}>
                  <TableCell><div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-muted-foreground" /><span className="font-medium">{a.label}</span></div>
                    <span className="text-xs text-muted-foreground">{a.properties.join(", ")}</span></TableCell>
                  <TableCell><Badge variant="secondary">{a.lots} lot(s)</Badge></TableCell>
                  <TableCell className="text-right font-medium">{eur(data.price_per_address)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {data.invoices.length > 0 && (
        <div className="rounded-xl border border-border bg-card overflow-hidden mt-6">
          <div className="px-5 py-4 border-b border-border"><h3 className="font-heading font-semibold">Historique des factures</h3></div>
          <Table>
            <TableHeader><TableRow><TableHead>Année</TableHead><TableHead>Adresses</TableHead><TableHead>Montant</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.invoices.map((inv) => (
                <TableRow key={inv.id}>
                  <TableCell className="font-mono-plex">{inv.period}</TableCell>
                  <TableCell>{inv.nb_addresses}</TableCell>
                  <TableCell>{eur(inv.amount)}</TableCell>
                  <TableCell>
                    {inv.status === "payé"
                      ? <span className="inline-flex items-center gap-1 text-[hsl(var(--chart-2))] text-sm"><CheckCircle2 className="w-4 h-4" /> Payé</span>
                      : <Badge variant="secondary">{inv.status}</Badge>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
